import { useState } from "react";
import { toast } from "sonner";
import { api, publicQuotationUrl, API, updateQuotationStatus } from "@/lib/quotationApi";
import { buildQuotationShareText } from "@/lib/shareText";
import { exportQuotationDualPDFs } from "@/lib/pdfExport";
import { clearDraft } from "@/lib/draftStorage";

// All save/approve/share/download side-effects for the quotation flow.
// Receives current state + setters from QuotationFlow so the page component
// stays focused on step navigation and form state.
export function useQuotationActions({
  data,
  branding,
  logoUrl,
  paymentPlans,
  savedId,
  savedStatus,
  savedTiersSnapshot,
  effectiveTierConfigV2,
  activity,
  setSavedId,
  setSavedStatus,
  setSavedTiersSnapshot,
  setApprovedByName,
  setApprovedAt,
  setActivity,
  setDirty,
}) {
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const buildPayload = () => ({
    customer: data.customer,
    rooms: data.rooms,
    accessories: (data.accessories || []).filter((a) => (a.quantity || 0) > 0),
    remarks: data.remarks,
    discount_amount: parseFloat(data.discount_amount) || 0,
    tax_percent: parseFloat(data.tax_percent) || 0,
    selected_tier_ids: data.selected_tier_ids || [],
    payment_plan_id: data.payment_plan_id || null,
  });

  const onApprove = async () => {
    if (!savedId || approving) return;
    setApproving(true);
    try {
      const r = await updateQuotationStatus(savedId, "approved");
      setSavedStatus(r.data?.status || "approved");
      setApprovedByName(r.data?.approved_by_name || null);
      setApprovedAt(r.data?.approved_at || null);
      setActivity(r.data?.activity || activity);
      toast.success("Quotation approved — public link is now live");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally {
      setApproving(false);
    }
  };

  const onUnapprove = async () => {
    if (!savedId || approving) return;
    setApproving(true);
    try {
      const r = await updateQuotationStatus(savedId, "draft");
      setSavedStatus(r.data?.status || "draft");
      setApprovedByName(null);
      setApprovedAt(null);
      setActivity(r.data?.activity || activity);
      toast.success("Approval removed — public link is now offline");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setApproving(false);
    }
  };

  const saveQuotation = async () => {
    setSaving(true);
    try {
      const r = await api.post("/quotations", buildPayload());
      setSavedId(r.data.id);
      setSavedStatus(r.data.status || "draft");
      setActivity(r.data.activity || []);
      setSavedTiersSnapshot(r.data.tiers || null);
      setDirty(false);
      toast.success("Quotation saved");
      clearDraft();
    } catch (e) {
      console.error("Save quotation failed:", e);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateQuotation = async () => {
    if (!savedId) return;
    setSaving(true);
    try {
      const r = await api.put(`/quotations/${savedId}`, buildPayload());
      setSavedTiersSnapshot(r.data.tiers || null);
      const wasApproved = savedStatus === "approved";
      const newStatus = r.data.status || savedStatus;
      setSavedStatus(newStatus);
      setApprovedByName(r.data.approved_by_name || null);
      setApprovedAt(r.data.approved_at || null);
      setActivity(r.data.activity || activity);
      setDirty(false);
      if (wasApproved && newStatus !== "approved") {
        toast.warning("Changes saved — re-approval required to publish the updated quote");
      } else {
        toast.success("Changes saved");
      }
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const buildShareText = () =>
    buildQuotationShareText({
      data,
      branding,
      paymentPlans,
      savedId,
      savedStatus,
      savedTiersSnapshot,
      effectiveTierConfigV2,
    });

  const onShareWhatsApp = () => {
    const text = encodeURIComponent(buildShareText());
    const phone = (data.customer.mobile || "").replace(/[^0-9]/g, "");
    const url = phone
      ? `https://wa.me/91${phone}?text=${text}`
      : `https://wa.me/?text=${text}`;
    window.open(url, "_blank");
  };

  const onDownloadPDF = async () => {
    if (downloading) return;
    const approved = savedStatus === "approved";
    // Only an approved quote has a live public link; unapproved internal
    // downloads render from local data and carry a "NOT APPROVED" stamp.
    const shareUrl = savedId && approved ? publicQuotationUrl(savedId) : "";
    setDownloading(true);
    toast.message("Preparing PDFs…", {
      description: "Color + B&W copies will start downloading shortly.",
    });
    try {
      await exportQuotationDualPDFs(data, savedId, shareUrl, {
        tierConfigV2: effectiveTierConfigV2,
        tiersSnapshot: savedTiersSnapshot,
        selectedTierIds: data.selected_tier_ids,
      }, {
        branding,
        logoUrl,
        apiBase: API,
        approved,
      });
      toast.success("Both PDFs downloaded");
    } catch (e) {
      console.error("PDF generation failed:", e);
      toast.error("PDF generation failed");
    } finally {
      setDownloading(false);
    }
  };

  return {
    saving,
    approving,
    downloading,
    onApprove,
    onUnapprove,
    saveQuotation,
    updateQuotation,
    onShareWhatsApp,
    onDownloadPDF,
  };
}
